<template>
  <v-container>
    <v-form v-model="isFormValid" @submit.prevent="onSubmit">
      <v-sheet class="border-opacity-25 border-thin rounded-lg pa-5">
        <div class="text-h6 text-center font-weight-black">Branch Information</div>
        <v-row>
          <v-col cols="12" md="12">
            <v-text-field
              v-model="branch.name"
              label="Name"
              :rules="[rules.required]"
              :error-messages="errors.name"></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.city"
              label="City"
              :rules="[rules.required]"
              :error-messages="errors.city"></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.state"
              label="State"
              :rules="[rules.required]"
              :error-messages="errors.state"></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.website"
              label="Website"
              placeholder="https://www.72fest.com"
              hint="Full URL"
              :error-messages="errors.website"
              :rules="[rules.url]"></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.instagram"
              label="Instagram"
              :rules="[rules.instagram]"
              placeholder="72filmfest"
              hint="Instagram Username"
              :error-messages="errors.instagram"></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.facebook"
              label="Facebook"
              :rules="[rules.facebook]"
              placeholder="72filmfest"
              hint="Facebook Username"
              :error-messages="errors.facebook"></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.twitter"
              label="X"
              :rules="[rules.twitter]"
              placeholder="@72filmfest"
              hint="X Username starting with @"
              :error-messages="errors.twitter"></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.vimeo"
              label="Vimeo"
              :rules="[rules.vimeo]"
              placeholder="72filmfest"
              hint="Vimeo Username"
              :error-messages="errors.vimeo"></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="branch.youtube"
              label="YouTube Channel"
              :rules="[rules.youtube]"
              placeholder="72filmfest"
              hint="YouTube Channel Name"
              :error-messages="errors.youtube"></v-text-field>
          </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-row>
          <v-col cols="12" md="12"
            >Bio
            <Editor
              v-model="branch.bio"
              :init="{
                base_url: '/tinymce',
                suffix: '.min',
                plugins: 'lists link image table code',
                toolbar:
                  'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist | link image table | code',
                skin: 'oxide',
                content_css: 'default',
              }"
              licenseKey="gpl" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="allowClose">
            <v-btn
              color="secondary"
              block
              class="rounded-lg mb-8"
              elevation="2"
              @click="emit('branchEditCanceled')">
              Cancel
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              :loading="isSubmitting"
              :disabled="!isFormValid"
              color="primary"
              block
              class="rounded-lg mb-8"
              elevation="2"
              @click="onSubmit">
              {{ saveLabel }}
            </v-btn>
          </v-col>
        </v-row>
      </v-sheet>
    </v-form>
  </v-container>
</template>
<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import Editor from '@tinymce/tinymce-vue';
import { rules } from '@/helpers';
import { BranchDTO, IBranchDTO } from '@/dtos';
import { useBranchStore } from '@/stores/modules/admin';
import { SNACKSTATUS } from '@/stores/util';
import { useUpdateStatusStore } from '@/stores/modules';
import '@/components/tinymce/tinymce';
interface Props {
  branch: IBranchDTO;
  isCreate: boolean;
  allowClose: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(['branchEditComplete', 'branchEditCanceled', 'snack']);
const branchStore = useBranchStore();
const statusStore = useUpdateStatusStore();

const branch = ref<IBranchDTO>(new BranchDTO());
const key = computed(() => `branch-${branch.value.id}`);

watch(
  () => props.branch,
  (newVal) => {
    if (newVal) {
      branch.value = new BranchDTO(newVal);
    }
  },
  { immediate: true },
);
const isCreate = toRef(props, 'isCreate', false);
const allowClose = toRef(props, 'allowClose', false);

const isFormValid = ref<boolean>(false);
const isSubmitting = ref<boolean>(false);

const errors = ref({
  name: '',
  city: '',
  state: '',
  website: '',
  instagram: '',
  facebook: '',
  twitter: '',
  bio: '',
  vimeo: '',
  youtube: '',
});

const onSubmit = async () => {
  if (isFormValid) {
    if (isCreate.value) {
      await branchStore.create.mutateAsync(branch.value);
    } else {
      await branchStore.update.mutateAsync(branch.value);
    }
    emit('branchEditComplete');
  }
};

const status = computed(() => statusStore.getStatus(key.value));

watch(status, (value) => {
  if (!value) return;
  emit('snack', 'Branch', value, statusStore.getError(key.value));
});

watch(
  () => statusStore.getStatus(`branch-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `branch-create`;
    emit('snack', 'Branch', value, statusStore.getError(key));
  },
);
const saveLabel = computed(() => {
  if (isCreate.value == true) {
    return 'Create Branch';
  } else {
    return 'Save';
  }
});
</script>
<style scoped></style>
